import * as XLSX from 'xlsx-js-style';
import { formatFecha, getSemanaIso } from './dateFormat';
import {
  resolveExportAttrs,
  toLogicalExportId,
  toPercentValue,
  EXCEL_NUMFMT,
  type ExcelExportFormat,
  type ResolvedExportAttrs,
} from '../services/columnExportAttrs';

interface TableData {
  [key: string]: any;
}

interface ExportOptions {
  columns: any[];
  data: TableData[];
  filename: string;
  includeBuyboxVariants?: boolean;
  /**
   * Mapa opcional de id_catalogo → SKU. Si se provee, los valores del SKU editable
   * (que vive en el state de DataTable, no en `item.sku`) se inyectan al export.
   * Si no se provee, la columna SKU se exporta tomando `item.sku` directo y, si
   * resulta toda vacía, se omite del archivo final (Fix 1).
   */
  skusMap?: { [id_catalogo: string]: string };
  /**
   * PT-#70: doc columnConfig del cliente (core/native/tags + templates.publicaciones).
   * Se usa para resolver title/format/widthChars de export por columna vía la cadena
   * template.export ?? columnConfig.export ?? DEFAULT_EXPORT_ATTRS[id] ?? hardcoded.
   * Si no se provee → resolución cae a DEFAULT + fallback por header (comportamiento
   * seguro; % Desc igual sale como porcentaje por guessFormatByTitle).
   */
  columnConfig?: any;
}

/**
 * PT-#70: mapea el leaf id de una columna PUBS al id LÓGICO de columnExportAttrs.
 * La normalización física/nativo → lógico vive en el módulo compartido
 * (`toLogicalExportId` en columnExportAttrs.ts) para que PUBS y EXPORTAR usen el MISMO
 * mapa (sale_price→precio_venta, discount→descuento, etc.). Ver DEFAULT_EXPORT_ATTRS.
 */
const toLogicalId = (colId: string): string => toLogicalExportId(colId);

// Columnas que deben tratarse como precio (formato currency $)
const PRICE_COL_REGEX = /precio|cuota|^pu$|^pum$/i;
const PRICE_COL_IDS = new Set([
  'sale_price',
  'original_price',
  'discounted_price',
  'precio',
  'precio_desc',
  'precio_venta',
  'precio_cuota',
  'p_cuota',
  'pu',
  'pum',
]);

// Columnas que son enteros (no currency, no decimales). "Cuotas" es count, no precio.
const INTEGER_HEADER_REGEX = /^cuotas$/i;
const INTEGER_COL_IDS = new Set(['cuotas']);

// Headers que llevan link clickeable
const LINK_HEADER_REGEX = /^(imagen|url|link)$/i;

// Estilos
const HEADER_STYLE = {
  font: { bold: true, color: { rgb: '000000' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'D9D9D9' } },
  alignment: { vertical: 'center', horizontal: 'left' },
  border: {
    top: { style: 'thin', color: { rgb: 'BFBFBF' } },
    bottom: { style: 'thin', color: { rgb: 'BFBFBF' } },
    left: { style: 'thin', color: { rgb: 'BFBFBF' } },
    right: { style: 'thin', color: { rgb: 'BFBFBF' } },
  },
};

const LINK_STYLE = {
  font: { color: { rgb: '0563C1' }, underline: true },
};

// Formato currency es-AR forzado via LCID 2C0A: $999.999,99
// El [$-2C0A] le dice a Excel que use separadores de Argentina (. miles, , decimales)
// independiente del locale del sistema del usuario.
const PRICE_FORMAT = '[$-2C0A]"$"#,##0.00';
const INTEGER_FORMAT = '0';

/**
 * Parsea un valor heterogéneo (string con $ y separadores, number, object) a number.
 * Devuelve null si no es un precio válido (preservar celda vacía vs $0.00).
 */
const parsePrice = (raw: any): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;

  // Object con value
  if (typeof raw === 'object' && raw?.value !== undefined) {
    return parsePrice(raw.value);
  }

  if (typeof raw === 'number') {
    return isFinite(raw) ? raw : null;
  }

  const str = String(raw).trim();
  if (!str || str === '-' || str === 'N/A') return null;

  // Quitar $, espacios, NBSP. Mantener . , - dígitos.
  let cleaned = str.replace(/\$/g, '').replace(/\s|\u00A0/g, '').trim();

  // Si tiene tanto . como , — el último es el decimal
  const hasDot = cleaned.includes('.');
  const hasComma = cleaned.includes(',');

  if (hasDot && hasComma) {
    // El que aparece último es decimal
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      // Formato europeo 1.234.567,89
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato US 1,234,567.89
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasComma) {
    // Solo coma. Si hay exactamente 2 dígitos después de la última coma → decimal
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length === 2) {
      cleaned = parts[0].replace(/\./g, '') + '.' + parts[1];
    } else {
      // Coma como miles
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasDot) {
    // Solo puntos. Si es múltiple o tiene >2 dec → miles europeo (sin decimales)
    const parts = cleaned.split('.');
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3)) {
      cleaned = parts.join('');
    }
    // si es 1234.56 lo dejamos como decimal
  }

  const n = parseFloat(cleaned);
  return isFinite(n) ? n : null;
};

// El helper de formateo de fecha vive en `./dateFormat` (formatFecha) para
// reusar el MISMO formato en la columna Fecha de la tabla visible (DataTable).
// Import arriba. Comportamiento idéntico al helper original (issue #14).

const isHttpUrl = (val: any): boolean => {
  if (typeof val !== 'string') return false;
  const s = val.trim();
  return /^https?:\/\/\S+/i.test(s);
};

const isPriceColumn = (colId: string, header: string): boolean => {
  if (PRICE_COL_IDS.has(colId)) return true;
  if (PRICE_COL_REGEX.test(colId)) return true;
  if (PRICE_COL_REGEX.test(header)) return true;
  return false;
};

const isLinkHeader = (header: string): boolean => {
  return LINK_HEADER_REGEX.test(header.trim());
};

const colIndexToLetter = (idx: number): string => {
  let s = '';
  let n = idx;
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
};

export const exportToExcel = ({ columns, data, filename, includeBuyboxVariants, skusMap, columnConfig }: ExportOptions) => {
  // Filter out hidden columns, action columns, and columns with non-string headers
  // (render functions like the extended_detail lupa icon)
  const EXCLUDE_COL_IDS = new Set(['actions', 'selection', 'extended_detail', 'alert']);
  const visibleColumns = columns.filter((col: any) => {
    if (!col.getIsVisible()) return false;
    if (EXCLUDE_COL_IDS.has(col.id)) return false;
    // Skip columns where header is a function (React render) with no displayName
    const h = col.columnDef.header;
    if (typeof h === 'function' && !col.columnDef.displayName) return false;
    return true;
  });

  // Sort columns to ensure URL comes after title
  const sortedColumns = [...visibleColumns].sort((a, b) => {
    const aId = a.id;
    const bId = b.id;
    if (aId === 'url' && (bId === 'titulo' || bId === 'title')) return 1;
    if ((aId === 'titulo' || aId === 'title') && bId === 'url') return -1;
    return visibleColumns.indexOf(a) - visibleColumns.indexOf(b);
  });

  // --- Fix 1: detectar si la columna SKU debe omitirse ---
  // Para cada row buscamos el sku real: primero skusMap[id_catalogo], luego item.sku.
  // Si todos quedan vacíos / "-" / "Click para editar" → omitir.
  const skuColumn = sortedColumns.find(col => col.id === 'sku');
  let skipSkuColumn = false;
  if (skuColumn) {
    const anyDefined = data.some(item => {
      const idCat = item.id_catalogo ?? item.ID_CATALOGO ?? item.id;
      const fromMap = skusMap && idCat != null ? skusMap[String(idCat)] : undefined;
      const fromItem = item.sku;
      const raw = (fromMap ?? fromItem ?? '').toString().trim();
      if (!raw) return false;
      if (raw === '-' || raw === 'Click para editar') return false;
      return true;
    });
    skipSkuColumn = !anyDefined;
  }

  // Effective columns (sin SKU si corresponde)
  const effectiveColumns = skipSkuColumn
    ? sortedColumns.filter(col => col.id !== 'sku')
    : sortedColumns;

  // --- PT-#70: resolución de atributos de export (title/format/widthChars) ---
  // Cadena: template.publicaciones.export ?? columnConfig.export ?? DEFAULT[id] ?? hardcoded.
  const cc = columnConfig || {};
  const tplItems: any[] = cc?.templates?.publicaciones?.items || [];
  const tplExportById = new Map<string, any>(
    tplItems.filter((it: any) => it && it.export).map((it: any) => [it.id, it.export])
  );
  const ccExportFor = (logicalId: string): any =>
    cc?.core?.[logicalId]?.export ??
    cc?.native?.[logicalId]?.export ??
    cc?.tags?.[logicalId]?.export ??
    null;
  const rawHeaderOf = (col: any): string =>
    (typeof col.columnDef.header === 'string' ? col.columnDef.header : null) || col.id;
  const resolveForCol = (col: any): ResolvedExportAttrs => {
    const logicalId = toLogicalId(col.id);
    return resolveExportAttrs(logicalId, {
      templateExport: tplExportById.get(logicalId) ?? null,
      columnConfigExport: ccExportFor(logicalId),
      fallbackTitle: rawHeaderOf(col),
    });
  };
  // Título resuelto por columna → se usa como HEADER y como CLAVE de row (coherencia).
  const resolvedByColId = new Map<string, ResolvedExportAttrs>();
  effectiveColumns.forEach((col: any) => resolvedByColId.set(col.id, resolveForCol(col)));
  const titleOf = (col: any): string =>
    resolvedByColId.get(col.id)?.title || rawHeaderOf(col);
  // Formato/ancho por TÍTULO final (para el loop de celdas y !cols).
  const formatByTitle = new Map<string, ExcelExportFormat>();
  const widthByTitle = new Map<string, number>();
  effectiveColumns.forEach((col: any) => {
    const r = resolvedByColId.get(col.id);
    if (!r) return;
    formatByTitle.set(r.title, r.format);
    widthByTitle.set(r.title, r.widthChars);
  });
  // Columnas inyectadas (no salen de effectiveColumns): ID Pulpou y URL.
  const idPulpouAttrs = resolveExportAttrs('id_pulpou', {
    templateExport: tplExportById.get('id_pulpou') ?? null,
    columnConfigExport: ccExportFor('id_pulpou'),
    fallbackTitle: 'ID Pulpou',
  });
  const urlAttrs = resolveExportAttrs('url', {
    templateExport: tplExportById.get('url') ?? null,
    columnConfigExport: ccExportFor('url'),
    fallbackTitle: 'URL',
  });
  const ID_PULPOU_TITLE = idPulpouAttrs.title;
  const URL_TITLE = urlAttrs.title;
  formatByTitle.set(ID_PULPOU_TITLE, idPulpouAttrs.format);
  widthByTitle.set(ID_PULPOU_TITLE, idPulpouAttrs.widthChars);
  formatByTitle.set(URL_TITLE, urlAttrs.format);
  widthByTitle.set(URL_TITLE, urlAttrs.widthChars);

  // --- Construir rows ---
  const rows = data.map(item => {
    const row: any = {};

    const idCatalogo = item.id_catalogo ?? item.ID_CATALOGO ?? item.id ?? '';
    row[ID_PULPOU_TITLE] = idCatalogo !== '' ? Number(idCatalogo) || idCatalogo : '';

    // NOTA: la columna Fecha NO se inyecta acá como columna sintética. Fluye por
    // `effectiveColumns` (columna virtual `fecha` de DataTable, header 'Fecha') para
    // respetar el orden del columnConfig y evitar el duplicado 'fecha'+'Fecha'.
    // El formateo DD/MM/YYYY se aplica en el loop de columnas (rama colId === 'fecha').

    if (includeBuyboxVariants) {
      row['Tipo'] = item.is_buybox_variant ? 'Variante BuyBox' : 'Producto Principal';
      if (item.is_buybox_variant && item.Seller) {
        row['Vendedor'] = item.Seller;
      }
    }

    effectiveColumns.forEach(col => {
      const colId = col.id;
      // PT-#70: la clave de row = TÍTULO resuelto (mismo que el header final).
      const header = titleOf(col);
      const fmt = formatByTitle.get(header);

      let value: any;
      if (colId === 'fecha') {
        // Columna Fecha (virtual): formatear a DD/MM/YYYY con el MISMO helper que
        // la tabla visible (formatFecha). El accessorFn devuelve el ISO crudo.
        row[header] = formatFecha(item.fecha ?? item.Fecha ?? item.FECHA);
        return;
      } else if (colId === 'semana') {
        // PT-#63: Columna Semana (virtual): número de semana ISO 8601 `AAAA-Www`,
        // derivada del MISMO item.fecha vía getSemanaIso. Sin fecha → ''.
        row[header] = getSemanaIso(item.fecha ?? item.Fecha ?? item.FECHA);
        return;
      } else if (colId === 'sku') {
        // Tomar del map primero, sino item.sku
        const fromMap = skusMap && idCatalogo !== '' ? skusMap[String(idCatalogo)] : undefined;
        const fromItem = item.sku;
        const raw = (fromMap ?? fromItem ?? '').toString().trim();
        value = (!raw || raw === '-' || raw === 'Click para editar') ? '' : raw;
      } else if (col.columnDef.accessorFn) {
        value = col.columnDef.accessorFn(item);
      } else if (col.columnDef.accessorKey) {
        value = item[col.columnDef.accessorKey];
      } else {
        value = item[colId];
      }

      if (fmt === 'percent') {
        // PT-#70 #4: % Desc como fracción (40% → 0.4) con numFmt 0%. null → celda vacía.
        row[header] = toPercentValue(value);
      } else if (colId === 'imagen') {
        row[header] = value || '';
      } else if (colId === 'titulo' || colId === 'title') {
        if (typeof value === 'object' && value?.titulo) {
          if (item.is_buybox_variant) {
            row[header] = `  → ${value.titulo || ''}`;
          } else {
            row[header] = value.titulo || '';
          }
          row[URL_TITLE] = value.url || item.url || item.URL || '';
        } else {
          if (item.is_buybox_variant) {
            row[header] = `  → ${value || ''}`;
          } else {
            row[header] = value || '';
          }
          row[URL_TITLE] = item.url || item.URL || '';
        }
      } else if (colId === 'pagina_orden') {
        if (typeof value === 'object' && value) {
          const pagina = value.pagina || value.page || 0;
          const orden = value.orden || value.orden_en_pag || value.order || 0;
          row[header] = `${String(pagina).padStart(3, '0')}-${String(orden).padStart(3, '0')}`;
        } else {
          row[header] = '000-000';
        }
      } else if (fmt === 'integer' || INTEGER_COL_IDS.has(colId) || INTEGER_HEADER_REGEX.test(String(header))) {
        // Cuotas — entero, 0 decimales, sin $. Chequear ANTES de isPriceColumn
        // porque "cuotas" matchea PRICE_COL_REGEX (/cuota/) pero no es precio.
        const n = value != null && value !== '' ? parseInt(String(value), 10) : null;
        row[header] = isFinite(n as number) ? n : null;
      } else if (fmt === 'currency_ars' || isPriceColumn(colId, String(header))) {
        // Fix 2: precio como number (null si no es válido)
        const n = parsePrice(value);
        row[header] = n; // puede ser null → celda vacía
      } else if (colId === 'marca' || colId === 'sitio' || colId === 'categoria') {
        row[header] = value ? String(value) : '';
      } else if (colId === 'contenido_total') {
        // Bug 2: catalog.contenido es DECIMAL(15,3) → "15.000". Strip trailing zeros.
        if (value === null || value === undefined || value === '') {
          row[header] = '';
        } else {
          const n = Number(value);
          row[header] = Number.isFinite(n) ? n.toString() : String(value);
        }
      } else {
        row[header] = value || '';
      }
    });

    // Asegurar URL aunque no haya columna url visible
    if (!row[URL_TITLE] && !effectiveColumns.find(col => col.id === 'url')) {
      const hasTitle = effectiveColumns.find(col => col.id === 'titulo' || col.id === 'title');
      if (hasTitle) {
        row[URL_TITLE] = item.url || item.URL || '';
      }
    }

    return row;
  });

  // --- Headers ---
  const headers: string[] = [];
  headers.push(ID_PULPOU_TITLE);
  // 'Fecha' se agrega desde el loop de effectiveColumns (columna virtual `fecha`),
  // no acá — evita la doble columna 'fecha'+'Fecha' y respeta el orden del config.

  if (includeBuyboxVariants) {
    headers.push('Tipo');
    if (data.some(item => item.is_buybox_variant && item.Seller)) {
      headers.push('Vendedor');
    }
  }

  effectiveColumns.forEach(col => {
    // PT-#70: header = título resuelto (mismo que la clave de row).
    headers.push(titleOf(col));
    if (col.id === 'titulo' || col.id === 'title') {
      if (!effectiveColumns.find(c => c.id === 'url')) {
        headers.push(URL_TITLE);
      }
    }
  });

  // --- Worksheet ---
  const ws = XLSX.utils.json_to_sheet(rows, { header: headers });

  // Auto-adjust column widths.
  // OJO: NO usar `Math.max(header.length, ...rows.map(...))`. El spread expande una
  // entrada por fila como argumento de función; con exports multi-toma (~133k filas)
  // supera el máximo de argumentos del engine → `RangeError: Maximum call stack size
  // exceeded` (rompía Samsung + últimos 30 días = 31 tomas). Loop plano, sin spread.
  const colWidths = headers.map((header) => {
    // PT-#70: si hay ancho resuelto (template/columnConfig/DEFAULT) usarlo; sino
    // caer al heurístico por contenido (loop plano, sin spread — ver nota arriba).
    const resolvedW = widthByTitle.get(header);
    if (Number.isFinite(resolvedW as number) && (resolvedW as number) > 0) {
      return { wch: resolvedW as number };
    }
    let maxLength = header.length;
    for (const row of rows) {
      const v = row[header];
      if (v === null || v === undefined) continue;
      const len = String(v).length;
      if (len > maxLength) maxLength = len;
    }
    return { wch: Math.min(maxLength + 2, 50) };
  });
  ws['!cols'] = colWidths;

  // --- Aplicar estilos por celda ---
  // Map de header → columnIndex (para ubicar cells)
  const headerToCol: { [h: string]: number } = {};
  headers.forEach((h, i) => { headerToCol[h] = i; });

  // Fix 3: header row con bold + fondo gris claro
  headers.forEach((h, i) => {
    const addr = `${colIndexToLetter(i)}1`;
    if (!ws[addr]) ws[addr] = { t: 's', v: h };
    ws[addr].s = HEADER_STYLE;
  });

  // PT-#70: numFmt por header manejado por el FORMATO resuelto (title→format), con
  // fallback a las regex legacy para columnas sin formato resuelto (ej. columnas de
  // clientes no mapeadas). Enteros primero (Cuotas matchea /cuota/ pero es count).
  const legacyIntegerSet = new Set(headers.filter(h => INTEGER_HEADER_REGEX.test(h)));
  const legacyPriceSet = new Set(headers.filter(h => PRICE_COL_REGEX.test(h) && !legacyIntegerSet.has(h)));
  // header → numFmt de Excel (o null = string). Resuelve por formato, sino por regex.
  const numFmtByHeader = new Map<string, string | null>();
  headers.forEach((h) => {
    const fmt = formatByTitle.get(h);
    if (fmt) {
      numFmtByHeader.set(h, EXCEL_NUMFMT[fmt] ?? null);
    } else if (legacyIntegerSet.has(h)) {
      numFmtByHeader.set(h, INTEGER_FORMAT);
    } else if (legacyPriceSet.has(h)) {
      numFmtByHeader.set(h, PRICE_FORMAT);
    } else {
      numFmtByHeader.set(h, null);
    }
  });
  // header → es link. El FORMATO resuelto manda: 'hyperlink' → link; cualquier otro
  // formato explícito (ej. 'text', usado para deshabilitar url/imagen) gana sobre la regex
  // legacy. isLinkHeader() queda SOLO como fallback para columnas sin formato resuelto.
  const isLinkByHeader = new Map<string, boolean>();
  headers.forEach((h) => {
    const fmt = formatByTitle.get(h);
    isLinkByHeader.set(h, fmt === 'hyperlink' || (fmt == null && isLinkHeader(h)));
  });

  rows.forEach((row, rIdx) => {
    const excelRow = rIdx + 2; // header en row 1
    headers.forEach((h, cIdx) => {
      const addr = `${colIndexToLetter(cIdx)}${excelRow}`;
      const cell = ws[addr];
      if (!cell) return;
      const val = row[h];

      const numFmt = numFmtByHeader.get(h);
      // Celda numérica (integer/number/percent/currency_ars): val ya viene como number
      // (o null) desde el row-building. null/no-finito → celda vacía.
      if (numFmt) {
        if (val === null || val === undefined || val === '' || (typeof val === 'number' && !isFinite(val))) {
          cell.t = 's';
          cell.v = '';
          cell.z = undefined;
        } else if (typeof val === 'number') {
          cell.t = 'n';
          cell.v = val;
          cell.z = numFmt;
          cell.s = { ...(cell.s || {}), numFmt };
        }
      }

      // Links (Fix 4) — usar fórmula HYPERLINK en lugar de cell.l.
      // cell.l rompe el rels file con URLs largas/especiales (Excel hace recovery
      // y borra los links). HYPERLINK() vive en el sheet y es robusto.
      // Excel HYPERLINK limita cada argumento a 255 chars → si la URL supera, dejar plano.
      if (isLinkByHeader.get(h) && isHttpUrl(val)) {
        const url = String(val).trim();
        if (url.length <= 255) {
          const safe = url.replace(/"/g, '""');
          cell.f = `HYPERLINK("${safe}","${safe}")`;
          cell.t = 's';
          cell.v = url;
          cell.s = { ...(cell.s || {}), ...LINK_STYLE };
        }
      }
    });
  });

  // --- Workbook ---
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Productos');

  const fullFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, fullFilename);
};

export const generateDefaultFilename = (tableName: string, productCount?: number, tableDate?: string): string => {
  let date: Date;

  if (tableDate) {
    if (!tableDate.includes('T')) {
      const parts = tableDate.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      } else {
        console.warn('[ExportFilename] Invalid date format, using current date');
        date = new Date();
      }
    } else {
      date = new Date(tableDate);
    }

    if (isNaN(date.getTime())) {
      console.warn('[ExportFilename] Invalid date after parsing, using current date');
      date = new Date();
    }
  } else {
    date = new Date();
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const dd = pad(date.getUTCDate());
  const mm = pad(date.getUTCMonth() + 1);
  const yy = String(date.getUTCFullYear()).slice(-2);

  const now = new Date();
  const hhmm = `${pad(now.getHours())}${pad(now.getMinutes())}`;

  // Número de toma desde tableName (ej `toma_24_O6Blx...` → `24`). Fallback: vacío.
  const tomaMatch = String(tableName || '').match(/toma[_-]?(\d+)/i);
  const tomaNum = tomaMatch ? tomaMatch[1] : '';

  const middle = tomaNum ? `${tomaNum}_` : '';
  return `datos_toma_${middle}${dd}${mm}${yy}_${hhmm}`;
};

/**
 * Convert a human-readable filename into a safe, snake_case slug.
 * - Lowercase
 * - Spaces and separators (-) → _
 * - Strip filesystem-unsafe characters
 * - Collapse repeated underscores
 * - Trim leading/trailing underscores
 * Accents are preserved intentionally.
 */
export const slugifyFilename = (input: string): string => {
  return input
    .toLowerCase()
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};
